import React, { useState, useEffect } from 'react';
import './ThemeCustomizer.css';

const STOCK_WALLPAPERS = [
  { id: 'aurora', name: 'Aurora', url: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=1400&q=80' },
  { id: 'mountains', name: 'Mountains', url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1400&q=80' },
  { id: 'city-night', name: 'City Night', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1400&q=80' },
  { id: 'ocean', name: 'Ocean', url: 'https://images.unsplash.com/photo-1473116763249-2faaef81ccda?auto=format&fit=crop&w=1400&q=80' },
  { id: 'abstract', name: 'Abstract', url: 'https://images.unsplash.com/photo-1557672172-298e090bd0f1?auto=format&fit=crop&w=1400&q=80' },
];

const ThemeCustomizer = ({ settings, onSettingsChange }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [customWallpapers, setCustomWallpapers] = useState([]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('weta_custom_wallpapers');
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        setCustomWallpapers(parsed.filter((u) => typeof u === 'string' && u.startsWith('data:image')));
      }
    } catch {
      setCustomWallpapers([]);
    }
  }, []);

  const handleColorChange = (field, value) => {
    const newSettings = { ...localSettings, [field]: value };
    setLocalSettings(newSettings);
    onSettingsChange(field, value);
  };

  const handleWallpaperSelect = (url) => {
    const next = { ...localSettings, theme_wallpaper_url: url };
    setLocalSettings(next);
    onSettingsChange('theme_wallpaper_url', url);
  };

  const handleWallpaperModeChange = (mode) => {
    const next = { ...localSettings, theme_wallpaper_mode: mode };
    setLocalSettings(next);
    onSettingsChange('theme_wallpaper_mode', mode);
  };

  const handleCustomWallpaperUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      if (!dataUrl) return;
      setCustomWallpapers((prev) => {
        const next = [dataUrl, ...prev.filter((u) => u !== dataUrl)].slice(0, 8);
        try {
          localStorage.setItem('weta_custom_wallpapers', JSON.stringify(next));
        } catch {}
        return next;
      });
      handleWallpaperSelect(dataUrl);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const availableWallpapers = [
    ...STOCK_WALLPAPERS,
    ...customWallpapers.map((url, idx) => ({
      id: `custom-${idx}`,
      name: `Custom ${idx + 1}`,
      url,
    })),
  ];
  const hasCurrent = !!localSettings.theme_wallpaper_url;
  const currentInList = availableWallpapers.some((w) => w.url === localSettings.theme_wallpaper_url);
  if (hasCurrent && !currentInList) {
    availableWallpapers.unshift({
      id: 'current-custom',
      name: 'Current Custom',
      url: localSettings.theme_wallpaper_url,
    });
  }

  return (
    <div className="theme-customizer">
      <div className="theme-customizer-header">
        <h3>🎨 Theme Customization</h3>
        <p>Customize your chatbot's appearance</p>
      </div>

      <div className="theme-sections">
        {/* Chatbot Header Background Color */}
        <div className="theme-section">
          <h4>Chatbot Header Background Color</h4>
          <div className="setting-group">
            <label>Header Background</label>
            <input
              type="color"
              value={localSettings.theme_header_bg || '#2563eb'}
              onChange={(e) => handleColorChange('theme_header_bg', e.target.value)}
            />
          </div>
        </div>

        {/* App Background Color */}
        <div className="theme-section">
          <h4>App Background Color</h4>
          <div className="setting-group">
            <label>Chat Background</label>
            <input
              type="color"
              value={localSettings.theme_background || '#f9fafb'}
              onChange={(e) => handleColorChange('theme_background', e.target.value)}
            />
          </div>
        </div>

        {/* Bot Bubble Color */}
        <div className="theme-section">
          <h4>Bot Bubble Color</h4>
          <div className="setting-group">
            <label>Bot Bubble Background</label>
            <input
              type="color"
              value={localSettings.theme_bot_bubble_bg || '#ffffff'}
              onChange={(e) => handleColorChange('theme_bot_bubble_bg', e.target.value)}
            />
          </div>
        </div>

        {/* User Bubble Color */}
        <div className="theme-section">
          <h4>User Bubble Color</h4>
          <div className="setting-group">
            <label>User Bubble Background</label>
            <input
              type="color"
              value={localSettings.theme_user_bubble_bg || '#f8fafc'}
              onChange={(e) => handleColorChange('theme_user_bubble_bg', e.target.value)}
            />
          </div>
        </div>

        {/* Font Color */}
        <div className="theme-section">
          <h4>Font Color</h4>
          <div className="setting-group">
            <label>Text Color</label>
            <input
              type="color"
              value={localSettings.theme_text_color || '#2d3748'}
              onChange={(e) => handleColorChange('theme_text_color', e.target.value)}
            />
          </div>
        </div>

        {/* Chatbot Name Color */}
        <div className="theme-section">
          <h4>Chatbot Name Color</h4>
          <div className="setting-group">
            <label>Header Text Color</label>
            <input
              type="color"
              value={localSettings.theme_header_text || '#ffffff'}
              onChange={(e) => handleColorChange('theme_header_text', e.target.value)}
            />
          </div>
        </div>

        {/* Wallpaper */}
        <div className="theme-section">
          <h4>Chat Background Wallpaper</h4>
          <div className="wallpaper-grid">
            {availableWallpapers.map((wallpaper) => (
              <button
                key={wallpaper.id}
                type="button"
                className={`wallpaper-tile ${localSettings.theme_wallpaper_url === wallpaper.url ? 'active' : ''}`}
                onClick={() => handleWallpaperSelect(wallpaper.url)}
                title={wallpaper.name}
                style={{ backgroundImage: `url(${wallpaper.url})` }}
              >
                <span>{wallpaper.name}</span>
              </button>
            ))}
            <label className="wallpaper-upload-btn">
              + Custom
              <input type="file" accept="image/*" onChange={handleCustomWallpaperUpload} />
            </label>
            <button
              type="button"
              className="wallpaper-clear-btn"
              onClick={() => handleWallpaperSelect('')}
            >
              Clear
            </button>
          </div>

          <div className="setting-group">
            <label>Wallpaper Fit</label>
            <select
              value={localSettings.theme_wallpaper_mode || 'cover'}
              onChange={(e) => handleWallpaperModeChange(e.target.value)}
            >
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
            </select>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="theme-preview">
        <h4>Preview</h4>
        <div className="preview-container">
          <div 
            className="preview-header"
            style={{
              background: localSettings.theme_header_bg || '#2563eb',
              color: localSettings.theme_header_text || '#ffffff'
            }}
          >
            {localSettings.bot_name || 'AI Customer Support'}
          </div>
          <div 
            className="preview-messages"
            style={{
              background: localSettings.theme_background || '#f9fafb',
              backgroundImage: localSettings.theme_wallpaper_url ? `url(${localSettings.theme_wallpaper_url})` : 'none',
              backgroundSize: localSettings.theme_wallpaper_mode === 'contain' ? 'contain' : 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className="preview-message bot">
              <div 
                className="preview-bubble"
                style={{
                  background: localSettings.theme_bot_bubble_bg || '#ffffff',
                  color: localSettings.theme_text_color || '#2d3748'
                }}
              >
                Hello! How can I help you?
              </div>
            </div>
            <div className="preview-message user">
              <div 
                className="preview-bubble"
                style={{
                  background: localSettings.theme_user_bubble_bg || '#f8fafc',
                  color: localSettings.theme_text_color || '#ffffff'
                }}
              >
                Hi there!
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;

